import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input, Loader } from '@dreamtree-org/twreact-ui';
import {
  resolveCompany, describeInvitation, acceptInvitation, refreshMe, errorOf,
} from '@/lib/tenantAuth';
import AuthShell from './AuthShell';

/**
 * A11 — accept an invitation: `/c/:slug/accept-invite?token=…`. The invitation already
 * binds the branch and role; this only sets the person's name and password (15+ chars,
 * no composition rules — doc §Auth 2). An administrator lands on two-step setup next.
 */
export default function AcceptInvite() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const token = search.get('token') || '';
  const nav = useNavigate();
  const [preauthId, setPreauthId] = useState(null);
  const [invite, setInvite] = useState(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await resolveCompany(slug);
        setPreauthId(res.preauthId);
        const info = await describeInvitation(res.preauthId, token);
        setInvite(info);
        setFullName(info.fullName || '');
      } catch (ex) {
        setErr(errorOf(ex).message);
      }
    })();
  }, [slug, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setErr('The two passwords do not match');
      return;
    }
    setErr('');
    setBusy(true);
    try {
      const res = await acceptInvitation(preauthId, token, fullName, password);
      await refreshMe();
      nav(res.needsStrongFactor ? '/account/security' : '/', { replace: true });
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={`Join ${invite ? invite.company.displayName : 'your company'}`}>
      <div className="space-y-4">
        {err && <Alert variant="error">{err}</Alert>}
        {!invite && !err && <Loader />}
        {invite && (
          <form className="space-y-3" onSubmit={submit}>
            <p className="text-sm text-secondary-600">
              You&apos;re joining branch <strong>{invite.branch.name}</strong> as <strong>{invite.email}</strong>.
            </p>
            <Input label="Your name" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" required />
            <Input label="Choose a password (15+ characters)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={15} required />
            <Input label="Repeat password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
            <Button type="submit" className="w-full" disabled={busy}>{busy ? 'Joining…' : 'Accept invitation'}</Button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
